import {
  ClaimFormValues,
  HomeUserClaimFormValues,
} from "src/components/Form/Claim/types";
import { initialAppliances } from "src/components/Form/FormComponents/Calculator";
import { format } from "date-fns";
import {
  electricityInitialValues,
  formInitValues,
  gasInitialValues,
  internetInitValues,
} from "src/components/Form/FormComponents/Validations";
import {
  abstractServiceBlockValues,
  GasApiValidationResults,
  generateAddressString,
  generateBillingAddressString,
  getFilesByType,
  isEmptyString,
  mapGasOptionToConnectionType,
  populateTotalLoadDetails,
} from "src/components/Form/helper";
import { mapConnectionTypeToBlockName } from "src/components/Form/helpers";
import {
  AccountPayer,
  BillingAddressValues,
  ElectricityBlockValues,
  ErrorTypeEnum,
  FormValues,
  GasOptionEnum,
  InternetBlockValues,
  LodgedElectricityService,
  LodgeServiceType,
  PremiseAddressValues,
  Service,
} from "src/components/Form/types";
import {
  ConnectionTypeEnum,
  ConnectionTypeFilterOptionEnum,
  EICStatusEnum,
  OperationalTeamEnum,
  PropertyType,
  RequestFilter,
  RequestStatusEnum,
  RequestStatusFilterEnum,
  RequestStatusFilterOption,
  ServiceTypeEnum,
  State,
} from "src/components/Premises/types";
import {
  ElectricityService,
  GasServices,
  InternetService,
} from "src/context/FormContext";
import axios, { AxiosError } from "axios";
import { poBoxTypes } from "src/components/Form/FormComponents/AddressConstants";
import {
  BulkDuplicateFormValues,
  BulkDuplicatePremises,
} from "src/components/Premises/BulkDuplicateDialog/types";
import { UpdateEligibilityFormValues } from "src/components/Premises/UpdateEligibilityDialog/types";

export const getInternetDetails = (
  internetService: any,
  values: any,
  payload: any
) => {
  const data = {} as any;
  data.internetPreInstaller = internetService?.pre_installer
    ? internetService?.pre_installer
    : payload?.internetHomeUserPreInstaller
    ? payload?.internetHomeUserPreInstaller
    : null;
  if (internetService?.raw_request_status === "DRAFT") {
    // if draft then check if its MSA .. site_supervisor used, else if payload has internetSiteSupervisor then use that
    // if not found check payload for homeusersitesupervisor, then see if premise has site supervisor
    // else check if gasoptions has sitesupervisor
    data.internetSiteSupervisor = internetService?.site_supervisor
      ? internetService?.site_supervisor
      : payload?.internetHomeUserSiteSupervisor
      ? payload?.internetHomeUserSiteSupervisor
      : values.premise.site_details.site_supervisor
      ? values.premise.site_details.site_supervisor
      : null;
  } else {
    data.internetSiteSupervisor = internetService?.site_supervisor
      ? internetService?.site_supervisor
      : payload?.internetHomeUserSiteSupervisor;
  }
  return { ...data };
};

const gasOptionToLodgeServiceType: Record<GasOptionEnum, LodgeServiceType> = {
  [GasOptionEnum.GAS_LINE]: "GAS_LINE",
  [GasOptionEnum.GAS_METER]: "GAS_METER",
  [GasOptionEnum.BOTH]: "BOTH",
};

export const getGasService = (
  formValues: FormValues,
  gasOptionEnum: GasOptionEnum,
  services?: {
    electricityService?: ElectricityService;
    gasServices?: GasServices;
  },
  gasApiValidationResults?: GasApiValidationResults,
  autoSubmit?: boolean | false,
  homeUser?: boolean | false,
  lodgeServiceType?: LodgeServiceType
) => {
  const isDigitalEic =
    homeUser ||
    (formValues[gasOptionEnum].accountPayer === AccountPayer.SOMEONE_ELSE &&
      formValues.accountHolderDetailsType === "online");
  const currentEicStatus =
    formValues[gasOptionEnum].eicStatus === EICStatusEnum.EIC_ACCEPTED ||
    formValues[gasOptionEnum].eicStatus === EICStatusEnum.EIC_COMM_SENT
      ? formValues[gasOptionEnum].eicStatus
      : EICStatusEnum.EIC_COMM_NOT_SENT;
  return {
    service_type: "GAS",
    account_payer: formValues[gasOptionEnum].accountPayer,
    connection_type: mapGasOptionToConnectionType[gasOptionEnum],
    eic_status: isDigitalEic ? currentEicStatus : EICStatusEnum.EIC_COMM_UNUSED,
    auto_submit: isDigitalEic
      ? autoSubmit || services?.gasServices?.[gasOptionEnum]?.autoSubmit
      : false,
    payload: {
      ...formValues[gasOptionEnum],
      siteLockupDate:
        gasOptionEnum === GasOptionEnum.GAS_LINE
          ? normaliseDate(formValues[gasOptionEnum].siteLockupDate, null)
          : undefined,
      preferDate:
        gasOptionEnum === GasOptionEnum.GAS_METER
          ? normaliseDate(formValues[gasOptionEnum].preferDate, null)
          : undefined,
      gasOption: gasOptionEnum,
      connectionType: mapGasOptionToConnectionType[gasOptionEnum],
      ...(gasApiValidationResults?.[gasOptionEnum]
        ? gasApiValidationResults?.[gasOptionEnum]
        : {}),
    },
    //collaborator_id
    ...(isEmptyString(formValues[gasOptionEnum].collaboratorId)
      ? {}
      : { collaborator_id: formValues[gasOptionEnum].collaboratorId }),
    // for update premise: if services exist, add draft_id, draft_version, request_id
    ...(services?.gasServices?.[gasOptionEnum]
      ? {
          draft_id: services?.gasServices[gasOptionEnum]?.draftId,
          draft_version: services?.gasServices[gasOptionEnum]?.draftVersion,
          request_id: services?.gasServices[gasOptionEnum]?.requestId,
        }
      : {}),
    ...(gasOptionEnum === GasOptionEnum.GAS_METER &&
    formValues[gasOptionEnum]?.mirn
      ? { service_identifier: formValues[gasOptionEnum]?.mirn }
      : {}),
    // life_support
    ...(formValues[gasOptionEnum].accountPayer === AccountPayer.COMPANY &&
      (gasOptionEnum === GasOptionEnum.GAS_METER ||
        gasOptionEnum === GasOptionEnum.BOTH) && {
        life_support: formValues[gasOptionEnum]?.lifeSupport,
      }),
    // per-service lodged flag. Preserve from context; flip true only when this gas option is the lodge target.
    request_lodged:
      lodgeServiceType === gasOptionToLodgeServiceType[gasOptionEnum]
        ? true
        : services?.gasServices?.[gasOptionEnum]?.requestLodged ?? false,
  };
};

const normaliseInternetPreInstaller = (
  value: FormValues["internetPreInstaller"]
) => {
  if (!value) return null;

  if (typeof value === "string") {
    return value || null;
  }

  const hasAnyValue = [
    value.first_name,
    value.last_name,
    value.phone_number,
    value.email,
  ].some((field) => field !== undefined && field !== "");

  return hasAnyValue ? value : null;
};

const serviceTypesToServicesMapper = (
  formValues: FormValues,
  services?: {
    electricityService?: ElectricityService;
    gasServices?: GasServices;
    internetService?: InternetService;
  },
  gasApiValidationResults?: GasApiValidationResults,
  fromPremiseBlock?: boolean | false,
  gasOption?: "gasLine" | "gasMeter" | "both",
  autoSubmit?: boolean | false,
  homeUser?: boolean | false,
  lodgeServiceType?: LodgeServiceType
) => {
  const { gasOptions } = formValues;
  // create / update all selected gas blocks if fromPremiseBlock
  // otherwise only update one gas block if gasOption is provided
  const gasOptionsToProcess = fromPremiseBlock
    ? gasOptions
    : gasOption
    ? [gasOption as GasOptionEnum]
    : [];
  const gasServices = gasOptionsToProcess.map((g) =>
    getGasService(
      formValues,
      g,
      services,
      gasApiValidationResults,
      autoSubmit,
      homeUser,
      lodgeServiceType
    )
  );

  /* Start: gas meter will inherit the gas line values if gas line is created but gas meter is not created yet */
  const gasLineService = gasOptions.includes(GasOptionEnum.GAS_LINE)
    ? getGasService(
        formValues,
        GasOptionEnum.GAS_LINE,
        services,
        gasApiValidationResults,
        autoSubmit,
        homeUser,
        lodgeServiceType
      )
    : undefined;
  const gasMeterService = gasServices.find(
    (g) => g.connection_type === ConnectionTypeEnum.METER_FIX
  );
  if (
    gasLineService &&
    gasMeterService &&
    (gasLineService.draft_id || gasLineService.request_id) &&
    !gasMeterService.draft_id
  ) {
    gasMeterService.payload = {
      ...gasMeterService.payload,
      pressure: gasLineService.payload.pressure,
      otherPressure: gasLineService.payload.otherPressure,
      totalLoad: gasLineService.payload.totalLoad,
      totalLoadSummary: gasLineService.payload.totalLoadSummary,
      totalLoadDetails: [...(gasLineService.payload.totalLoadDetails || [])],
      appliancesChecklistFiles: [
        ...(gasLineService.payload.appliancesChecklistFiles || []),
      ],
      siteConditions: [...(gasLineService.payload.siteConditions || [])],
      otherSiteCondition: gasLineService.payload.otherSiteCondition,
      meterLocation: gasLineService.payload.meterLocation,
      otherMeterLocation: gasLineService.payload.otherMeterLocation,
      sitePlanFiles: [...(gasLineService.payload.sitePlanFiles || [])],
      isUpdatesOnGasLine: "no",
    };
  }
  /* End: gas meter will inherit the gas line values if gas line is created but gas meter is not created yet */
  const shouldUpdateElectricityService =
    fromPremiseBlock || services?.electricityService;
  const isElectricityServiceDigitalEic =
    homeUser ||
    (formValues.electricityAccountPayer === AccountPayer.SOMEONE_ELSE &&
      formValues.accountHolderDetailsType === "online");
  const currentDigitalEicStatus =
    formValues.electricityEicStatus === EICStatusEnum.EIC_ACCEPTED ||
    formValues.electricityEicStatus === EICStatusEnum.EIC_COMM_SENT
      ? formValues.electricityEicStatus
      : EICStatusEnum.EIC_COMM_NOT_SENT;
  const electricityService = shouldUpdateElectricityService
    ? {
        service_type: "ELECTRICITY",
        account_payer: formValues.electricityAccountPayer,
        connection_type: formValues.connectionType,
        home_user_asp: {
          first_name: formValues.homeUserAsp?.firstName,
          last_name: formValues.homeUserAsp?.lastName,
          license_number: formValues.homeUserAsp?.licenseNumber,
        },
        eic_status: isElectricityServiceDigitalEic
          ? currentDigitalEicStatus
          : EICStatusEnum.EIC_COMM_UNUSED,
        auto_submit: isElectricityServiceDigitalEic
          ? autoSubmit || services?.electricityService?.autoSubmit
          : false,
        payload: {
          ...abstractServiceBlockValues(formValues, electricityInitialValues),
        },
        //collaborator_id
        ...(isEmptyString(formValues.electricityCollaboratorId)
          ? {}
          : { collaborator_id: formValues.electricityCollaboratorId }),
        // for update premise: if services exist, add draft_id, draft_version, request_id
        ...(services?.electricityService
          ? {
              draft_id: services?.electricityService?.draftId,
              draft_version: services?.electricityService?.draftVersion,
              request_id: services?.electricityService?.requestId,
            }
          : {}),
        ...(isEmptyString(formValues.nmi)
          ? {}
          : { service_identifier: formValues.nmi }),
        // life_support
        ...(formValues.electricityAccountPayer === AccountPayer.COMPANY && {
          life_support: formValues?.electricityLifeSupport,
        }),
        //per-service lodged flag. Preserve prior value from context; flip true only when electricity is the lodge target.
        request_lodged:
          lodgeServiceType === "ELECTRICITY"
            ? true
            : services?.electricityService?.requestLodged ?? false,
      }
    : undefined;
  /* Start the Internet Service */
  const shouldUpdateInternetService =
    fromPremiseBlock || services?.internetService;
  const isInternetServiceDigitalEic =
    homeUser ||
    (formValues.internetAccountPayer === AccountPayer.SOMEONE_ELSE &&
      formValues.accountHolderDetailsType === "online");

  let connection_type =
    services?.internetService?.connectionType ??
    formValues.internetRequestStatus;
  if (
    connection_type === "PRE_INSTALL_NBN" &&
    formValues.questionnaireKnockdown === "yes"
  ) {
    connection_type = ConnectionTypeEnum.CAN_RETAIL;
  }
  const internetService = shouldUpdateInternetService
    ? {
        service_type: "INTERNET",
        account_payer: formValues.internetAccountPayer,
        service_identifier: formValues.locId,
        connection_type,
        pre_installer: homeUser
          ? undefined
          : normaliseInternetPreInstaller(formValues.internetPreInstaller),
        site_supervisor: homeUser
          ? undefined
          : formValues.internetSiteSupervisor,
        eic_status: isInternetServiceDigitalEic
          ? formValues.internetEicStatus ?? EICStatusEnum.EIC_COMM_NOT_SENT
          : EICStatusEnum.EIC_COMM_NOT_SENT,
        auto_submit: isInternetServiceDigitalEic
          ? autoSubmit || services?.internetService?.autoSubmit
          : false,
        payload: homeUser
          ? {
              ...abstractServiceBlockValues(formValues, internetInitValues),
              internetPreInstaller: normaliseInternetPreInstaller(
                formValues.internetPreInstaller
              ),
              internetHomeUserPreInstaller: normaliseInternetPreInstaller(
                formValues.internetPreInstaller
              ),
              internetHomeUserSiteSupervisor: formValues.internetSiteSupervisor,
            }
          : {
              ...abstractServiceBlockValues(formValues, internetInitValues),
            },
        //collaborator_id
        ...(isEmptyString(formValues.internetCollaboratorId)
          ? {}
          : { collaborator_id: formValues.internetCollaboratorId }),
        // for update premise: if services exist, add draft_id, draft_version, request_id
        ...(services?.internetService
          ? {
              draft_id: services?.internetService?.draftId,
              draft_version: services?.internetService?.draftVersion,
              request_id: services?.internetService?.requestId,
            }
          : {}),
        // Handover and Preinstallation Dates
        handover_date: normaliseDate(
          formValues?.internetHandoverDate
            ? new Date(formValues.internetHandoverDate)
            : null,
          null
        ),
        preinstallation_date: normaliseDate(
          formValues?.internetPreinstallationDate &&
            formValues.internetPreinstallationDate !== ""
            ? new Date(formValues.internetPreinstallationDate)
            : null,
          null
        ),
        // knockdown_future_state - only for ADDRESS_NOT_FOUND
        ...(formValues.internetKnockdownFutureState &&
        connection_type === "ADDRESS_NOT_FOUND"
          ? {
              knockdown_future_state: formValues.internetKnockdownFutureState,
            }
          : connection_type === "ADDRESS_NOT_FOUND" &&
            formValues.questionnaireKnockdown
          ? {
              knockdown_future_state:
                formValues.questionnaireKnockdown === "no"
                  ? "PRE_INSTALL_NBN"
                  : "CAN_RETAIL",
            }
          : {}),
        ...(formValues?.internetAnexAddress
          ? { anex_address: formValues.internetAnexAddress }
          : {}),
        // In case of ADDRESS_NOT_FOUND key field should be nbn_address instead of anex_address
        // LOC-1662: per-service lodged flag. Preserve prior value from context; flip true only when internet is the lodge target.
        request_lodged:
          lodgeServiceType === "INTERNET"
            ? true
            : services?.internetService?.requestLodged ?? false,
      }
    : undefined;
  return [
    ...(shouldUpdateElectricityService &&
    formValues.selectedConnections.includes("Electricity")
      ? [electricityService]
      : []),
    ...(formValues.selectedConnections.includes("Gas") ? gasServices : []),
    ...(shouldUpdateInternetService &&
    formValues.selectedConnections.includes("Internet")
      ? [internetService]
      : []),
  ];
};

export const premiseFormFieldMapper = (
  values: FormValues,
  services?: {
    electricityService?: ElectricityService;
    gasServices?: GasServices;
    internetService?: InternetService;
  },
  gasApiValidationResults?: GasApiValidationResults,
  fromPremiseBlock?: boolean | false,
  gasOption?: "gasLine" | "gasMeter" | "both",
  autoSubmit?: boolean | false,
  homeUser?: boolean | false,
  // LOC-1662: identifies which single service is being lodged on this save. Other services preserve their flag from the services arg.
  lodgeServiceType?: LodgeServiceType
) => {
  const { premiseAddress } = values;
  const isAnyAccountPayerSomeoneElse = [
    values.electricityAccountPayer,
    values.gasLine.accountPayer,
    values.gasMeter?.accountPayer,
    values.both?.accountPayer,
    values.internetAccountPayer,
  ].includes(AccountPayer.SOMEONE_ELSE);
  return {
    premise: {
      property_type: values.propertyType,
      id: values["id"],
      unit_type:
        values.propertyType === PropertyType.RESIDENTIAL &&
        premiseAddress["unitNumber"]
          ? "Unit"
          : premiseAddress["premiseType"],
      unit_number: premiseAddress["unitNumber"],
      floor_number: premiseAddress["level"],
      site_details: {
        id: premiseAddress.siteId,
        lot_number: premiseAddress["lotNumber"],
        street_number: premiseAddress["streetNumber"],
        // streetNumberSuffix
        street_name: premiseAddress["streetName"],
        street_suffix: premiseAddress["streetSuffix"],
        street_type: premiseAddress["streetType"],
        postcode: premiseAddress["postcode"],
        dp_number: premiseAddress["dpNumber"],
        block_number: premiseAddress["blockNumber"],
        section_number: premiseAddress["sectionNumber"],
        suburb: premiseAddress["suburb"],
        state: premiseAddress["state"],
        building_name: premiseAddress["buildingName"],
        requesting_company: {
          msa_number: values.requestingCompany?.msaNumber,
        },
        project: { id: values.projectId },
        site_supervisor: values.siteSupervisor,
        site_access: values.propertyAccess === "yes",
        site_access_details: values.preventingAccess,
      },
    },
    account: accountFormFieldMapper(
      values,
      fromPremiseBlock,
      isAnyAccountPayerSomeoneElse
    ),
    services: serviceTypesToServicesMapper(
      values,
      services,
      gasApiValidationResults,
      fromPremiseBlock,
      gasOption,
      autoSubmit,
      homeUser,
      lodgeServiceType
    ),
  };
};

export const accountFormFieldMapper = (
  values: FormValues | ClaimFormValues | HomeUserClaimFormValues,
  fromPremiseBlock?: boolean | false,
  isAnyAccountPayerSomeoneElse?: boolean
) => {
  return {
    id: values.accountId,
    first_name: values.accountPrimaryFirstName,
    last_name: values.accountPrimaryLastName,
    company_name: values.companyName,
    company_number: values.abn,
    communication_preference: values.accountCommsPrefs,
    phone_number: values.accountPrimaryPhone,
    email: values.accountPrimaryEmail,
    date_of_birth: normaliseDate(values.accountPrimaryDob, ""),
    postal_address_same_as_premise: values.sameAsConnectionAddress,
    postal_address: {
      street_number: values["billing-address"].streetNumber,
      // streetNumberSuffix
      street_name: values["billing-address"].streetName,
      street_suffix: values["billing-address"].streetSuffix,
      street_type: values["billing-address"].streetType,
      unit_type: values["billing-address"].premiseType,
      floor_number: values["billing-address"].level,
      unit_number: values["billing-address"].unitNumber,
      postcode: values["billing-address"].postcode,
      suburb: values["billing-address"].suburb,
      state: values["billing-address"].state,
      building_name: values["billing-address"].buildingName,
      postal_delivery_type: values["billing-address"].poboxType,
      postal_delivery_number_value: values["billing-address"].poboxNumber,
    },
    secondary_account: {
      add_second_name_to_bill: values.addSecondaryNameToBill,
      first_name: values.accountSecondaryFirstName,
      last_name: values.accountSecondaryLastName,
      phone_number: values.accountSecondaryPhone,
      email: values.accountSecondaryEmail,
      date_of_birth: normaliseDate(values.accountSecondaryDob, ""),
    },
    is_digital_eic: fromPremiseBlock
      ? isAnyAccountPayerSomeoneElse &&
        values.accountHolderDetailsType === "online"
      : values.accountHolderDetailsType === "online",
  };
};

export const getSolarInitialValues = (serviceType: string, state: State) => {
  if (serviceType !== "ELECTRICITY") return {};

  return [State.NSW, State.SA].includes(state)
    ? {}
    : { solar: "no", sizeOfSolar: "" };
};

export const sanitizeDuplicateSourceRequestData = (values: any): any => {
  return {
    property_type: values.property_type,
    premise: {
      ...values.premise,
      id: undefined,
      site_details: {
        ...values.premise.site_details,
        id: undefined,
        dp_number: values.premise.site_details?.dp_number?.replace("DP", ""),
      },
    },
    services: values.services
      .filter(
        (s: Service) => s.channel != "CLAIM" && s.service_type !== "INTERNET"
      )
      .map((s: Service) => ({
        service_type: s.service_type,
        account_payer: s.account_payer,
        connection_type: s.connection_type,
        request_type: s.request_type,
        raw_request_status: RequestStatusEnum.DRAFT,
        auto_submit: false,
        eic_status: EICStatusEnum.EIC_COMM_UNUSED,
        comm_sent_date_time: s.comm_sent_date_time,
      })),
  };
};

export const formDataToFormikMapper = (values: any): FormValues => {
  const electricityService = values.services.find(
    (s: LodgedElectricityService) => s.service_type === "ELECTRICITY"
  );
  const electricityAttachments = electricityService?.attachments_meta ?? [];
  const accountAttachments = values.account?.attachments_meta ?? [];
  const allFiles = getFilesByType([
    ...electricityAttachments,
    ...accountAttachments,
  ]);

  //gas has multiple sections based on gasOptions
  const gasServices = values.services.filter(
    (s: Service) => s.service_type === "GAS"
  );

  const internetService = values.services.find(
    (s: Service) => s.service_type === "INTERNET"
  );
  const electricityServicePayload: ElectricityBlockValues =
    electricityService && electricityService.payload
      ? typeof electricityService.payload === "string"
        ? JSON.parse(electricityService.payload)
        : electricityService.payload
      : {};
  const internetServicePayload: InternetBlockValues =
    internetService && internetService.payload
      ? typeof internetService.payload === "string"
        ? JSON.parse(internetService.payload)
        : internetService.payload
      : {};
  const gasServicesPayload =
    gasServices.length > 0 &&
    gasServices.reduce((acc: any, g: any) => {
      const gasAttachments = g?.attachments_meta ?? [];
      const gasServiceFiles = getFilesByType([...gasAttachments]);
      const payload = g.payload ? JSON.parse(g.payload) : {};
      return {
        ...acc,
        [mapConnectionTypeToBlockName(g.connection_type)]: {
          ...gasInitialValues[mapConnectionTypeToBlockName(g.connection_type)],
          ...payload,
          ...(payload.siteLockupDate
            ? { siteLockupDate: new Date(payload.siteLockupDate) }
            : {}),
          ...(payload.preferDate
            ? { preferDate: new Date(payload.preferDate) }
            : {}),
          ...(payload.totalLoadDetails && payload.totalLoadDetails.length > 0
            ? {
                totalLoadDetails: populateTotalLoadDetails(
                  payload.totalLoadDetails
                ),
              }
            : { totalLoadDetails: initialAppliances }),
          accountPayer: g?.account_payer,
          eicStatus: g?.eic_status,
          //lodged gas request metadata
          requesterName: g?.requestor,
          submittedDateTime: g?.submitted_date_time
            ? new Date(g.submitted_date_time)
            : undefined,
          allocateNmiOrderNumber: g?.service_identifier,
          meterServiceOrderNumber: g?.meter_service_order_number,
          supplyServiceOrderNumber: g?.supply_service_order_number,
          networkFormNumber: g?.network_form_number,
          appliancesChecklistFiles: gasServiceFiles.appliancesChecklistFiles,
          sitePlanFiles: gasServiceFiles.sitePlanFiles,
          shipperDocumentsFiles: gasServiceFiles.shipperDocumentsFiles,
          lifeSupport: g?.life_support,
        },
      };
    }, {});
  const determineSecondaryAccountHolder =
    values.account && values.account.secondary_account
      ? Object.values(values.account?.secondary_account).some(Boolean)
      : false;

  const determinedAccountPayer = () => {
    if (values.account?.company_number || values.account?.company_name) {
      return "company";
    }
    return "person";
  };
  const premiseAddress: PremiseAddressValues = {
    siteId: values.premise.site_details.id,
    state: values.premise.site_details.state
      ? values.premise.site_details.state
      : "",
    buildingName: values.premise.site_details.building_name
      ? values.premise.site_details.building_name
      : "",
    suburb: values.premise.site_details.suburb
      ? values.premise.site_details.suburb
      : "",
    postcode: values.premise.site_details.postcode
      ? values.premise.site_details.postcode
      : "",
    unitNumber: values.premise["unit_number"],
    premiseType: values.premise["unit_type"],
    streetNumber: values.premise.site_details.street_number,
    lotNumber: values.premise.site_details.lot_number,
    streetName: values.premise.site_details.street_name,
    grannyFlat: values.premise.site_details.granny_flat,
    level: values.premise["floor_number"],
    dpNumber: values.premise.site_details.dp_number,
    streetSuffix: values.premise.site_details.street_suffix,
    streetType: values.premise.site_details.street_type,
    sectionNumber: values.premise.site_details.section_number,
    blockNumber: values.premise.site_details.block_number,
  };

  const billingAddress: BillingAddressValues = {
    state: values.account?.postal_address?.state,
    buildingName: values.account?.postal_address?.building_name,
    suburb: values.account?.postal_address?.suburb,
    postcode: values.account?.postal_address?.postcode,
    unitNumber: values.account?.postal_address?.unit_number,
    premiseType: values.account?.postal_address?.unit_type,
    streetNumber: values.account?.postal_address?.street_number,
    streetName: values.account?.postal_address?.street_name,
    level: values.account?.postal_address?.floor_number,
    streetSuffix: values.account?.postal_address?.street_suffix,
    streetType: values.account?.postal_address?.street_type,
    poboxType: values.account?.postal_address?.postal_delivery_type,
    poboxNumber: values.account?.postal_address?.postal_delivery_number_value,
    pobox: (() => {
      const postalType = values.account?.postal_address?.postal_delivery_type;
      const postalNumber =
        values.account?.postal_address?.postal_delivery_number_value;
      const poBoxType = poBoxTypes.find((type) => type.value === postalType);

      return poBoxType
        ? poBoxType.optionalPoNumber || Boolean(postalNumber)
        : false;
    })(),
  };
  const billingAddressString = generateBillingAddressString(billingAddress);
  const isNonOnlineAccountHolderDetailsUpdated =
    !!values.account?.company_name ||
    !!values.account?.company_number ||
    !!values.account?.date_of_birth ||
    !!values.account?.phone_number ||
    !!values.account?.secondary_account?.first_name ||
    !!values.account?.secondary_account?.last_name ||
    !!values.account?.secondary_account?.email ||
    !!values.account?.secondary_account?.date_of_birth ||
    !!values.account?.secondary_account?.phone_number ||
    accountAttachments.length > 0 ||
    !!billingAddressString;
  const isEveryServiceEicStatusUnused = values.services.every(
    (s: Service) => s.eic_status === EICStatusEnum.EIC_COMM_UNUSED
  );
  // if account section is not present, default should be online mode
  const defaultIsDigitalEIC =
    isNonOnlineAccountHolderDetailsUpdated && isEveryServiceEicStatusUnused
      ? "pdf"
      : "online";

  const locId = values.services.find(
    (s: Service) => s.service_type === "INTERNET"
  )?.service_identifier;
  return {
    ...formInitValues,
    id: values.premise.id ? values.premise.id : "",
    siteSupervisor: values.premise.site_details.site_supervisor
      ? values.premise.site_details.site_supervisor
      : "",
    requestingCompany: values.premise.site_details.requesting_company
      ? {
          msaNumber: values.premise.site_details.requesting_company.msa_number,
          name: values.premise.site_details.requesting_company.name,
          id: values.premise.site_details.requesting_company.id,
          operationalTeam:
            values.premise.site_details.requesting_company.operational_team,
        }
      : null,
    projectId: values.premise.site_details.project
      ? values.premise.site_details.project.id
      : "",
    projectName: values.premise.site_details.project
      ? values.premise.site_details.project.name
      : "",
    propertyType: values.property_type ? values.property_type : "",
    locId: locId ?? "",
    premiseAddress,
    premiseAddressString: generateAddressString(premiseAddress),
    propertyAccess: values.premise.site_details.site_access ? "yes" : "no",
    preventingAccess: values.premise.site_details.site_access_details,
    serviceAssignee: "",
    accountId: values.account ? values.account.id : "",
    accountHolder: determinedAccountPayer(),
    accountPrimaryFirstName: values.account ? values.account.first_name : "",
    accountPrimaryLastName: values.account ? values.account.last_name : "",
    accountCommsPrefs:
      values.account && values.account.communication_preference
        ? values.account.communication_preference
        : "post",
    companyName: values.account ? values.account.company_name : "",
    abn: values.account ? values.account.company_number : "",
    accountPrimaryPhone: values.account ? values.account.phone_number : "",
    accountPrimaryEmail: values.account ? values.account.email : "",
    secondaryAccountHolder: determineSecondaryAccountHolder,
    "billing-address": billingAddress,
    billingAddressString,
    addSecondaryNameToBill:
      values.account && values.account.secondary_account
        ? values.account.secondary_account.add_second_name_to_bill
        : "",
    accountPrimaryDob:
      values.account && values.account.date_of_birth
        ? new Date(values.account.date_of_birth)
        : null,
    accountSecondaryFirstName:
      values.account && values.account.secondary_account
        ? values.account.secondary_account.first_name
        : "",
    accountSecondaryLastName:
      values.account && values.account.secondary_account
        ? values.account.secondary_account.last_name
        : "",
    accountSecondaryPhone:
      values.account && values.account.secondary_account
        ? values.account.secondary_account.phone_number
        : "",
    accountSecondaryEmail:
      values.account && values.account.secondary_account
        ? values.account.secondary_account.email
        : "",
    accountSecondaryDob:
      values.account &&
      values.account.secondary_account &&
      values.account.secondary_account.date_of_birth
        ? new Date(values.account.secondary_account.date_of_birth)
        : null,
    selectedConnections: [
      electricityService && "Electricity",
      gasServices.length > 0 && "Gas",
      internetService && "Internet",
    ].filter(Boolean),
    sameAsConnectionAddress: values?.account?.postal_address_same_as_premise,
    //electricity block
    ...electricityServicePayload,
    connectionType: electricityService?.connection_type,
    //electricity account payer does not come from payload object after submission,
    electricityEicStatus: electricityService?.eic_status,
    electricityAccountPayer: electricityService?.account_payer
      ? electricityService?.account_payer
      : AccountPayer.COMPANY,
    //lodged electricity request metadata
    electricityRequesterName: electricityService?.requestor,
    electricityRequesterFirstName: electricityService?.requestor_first_name,
    electricityRequesterLastName: electricityService?.requestor_last_name,
    electricityRequesterEmail: electricityService?.requestor_email,
    electricitySubmittedDateTime: electricityService?.submitted_date_time
      ? new Date(electricityService.submitted_date_time)
      : undefined,
    electricityLifeSupport: electricityService?.life_support,
    allocateNmiOrderNumber: electricityService?.allocate_nmi_order_number,
    meterServiceOrderNumber: electricityService?.meter_service_order_number,
    electricitySupplyServiceOrderNumber:
      electricityService?.supply_service_order_number,
    electricityNetworkFormNumber: electricityService?.network_form_number,
    ...gasServicesPayload,
    gasOptions: gasServicesPayload ? Object.keys(gasServicesPayload) : [],
    state: values.premise.site_details.state
      ? values.premise.site_details.state
      : "",
    nmi: electricityService?.service_identifier,
    // FOR INTERNET
    ...internetServicePayload,
    internetAccountPayer: internetService?.account_payer
      ? internetService?.account_payer
      : AccountPayer.COMPANY,
    //lodged internet request metadata
    internetRequesterName: internetService?.requestor,
    internetRequesterFirstName: internetService?.requestor_first_name,
    internetRequesterLastName: internetService?.requestor_last_name,
    internetRequesterEmail: internetService?.requestor_email,
    internetSubmittedDateTime: internetService?.submitted_date_time
      ? new Date(internetService.submitted_date_time)
      : undefined,
    internetHandoverDate: internetService?.handover_date,
    internetJobId: internetService?.preinstall_request_reference_id,
    internetRequestId: internetService?.request_id,
    internetNbnAddress: internetService?.nbn_address,
    internetEicStatus: internetService?.eic_status,
    internetRequestRawStatus: internetService?.raw_request_status,
    internetComputedRequestStatus: internetService?.request_status,
    internetPreinstallationDate: internetService?.preinstallation_date,
    internetCloseByName: `${internetService?.preinstallation_submission?.first_name} ${internetService?.preinstallation_submission?.last_name}`,
    internetCloseByDate: internetService?.preinstallation_submission?.date
      ? new Date(internetService.preinstallation_submission.date)
      : undefined,
    internetCancelByName: `${internetService?.cancelled_by_bspec?.first_name} ${internetService?.cancelled_by_bspec?.last_name}`,
    internetCancelByDate: internetService?.cancelled_by_bspec?.date
      ? new Date(internetService.cancelled_by_bspec.date)
      : undefined,
    internetKnockdownFutureState: internetService?.knockdown_future_state,
    internetAnexAddress: internetService?.anex_address,
    ...getInternetDetails(internetService, values, internetServicePayload),

    // INTERNET END
    /* accountHolderDetailsType:
      (!values.account || isNonOnlineAccountHolderDetailsUpdated) &&
      isEveryServiceEicStatusUnused
        ? "pdf"
        : "online", */
    accountHolderDetailsType: values.account
      ? values.account.is_digital_eic
        ? "online"
        : "pdf"
      : defaultIsDigitalEIC,
    ...allFiles,
  } as unknown as FormValues;
};

export const getErrorTypeEnum = (e: any): ErrorTypeEnum | undefined => {
  if (!axios.isAxiosError(e)) return;
  const axiosError = e as AxiosError;
  const errorCode = axiosError.response?.status;
  if (!errorCode) return;
  return errorCode === 503
    ? ErrorTypeEnum.KRAKEN_TIMEOUT
    : ErrorTypeEnum.KRAKEN_FIELD_VALIDATION;
};

export const getLodgeErrorTypeEnum = (e: any): ErrorTypeEnum => {
  if (!axios.isAxiosError(e)) return ErrorTypeEnum.LODGE_REQUEST_FAILED;
  const status = (e as AxiosError).response?.status;
  if (status === 503) return ErrorTypeEnum.KRAKEN_TIMEOUT;
  if (status && status >= 400 && status < 500) {
    return ErrorTypeEnum.LODGE_REQUEST_FAILED;
  }
  return ErrorTypeEnum.KRAKEN_FIELD_VALIDATION;
};

export const serviceTypesToPlanText = (values: string[]) => {
  return values.toString();
};

export const normaliseDate = (date: Date | null, defaultValue: null | "") => {
  return date ? format(date, "yyyy-MM-dd") : defaultValue;
};
// export const premiseAddressToBillingAddress = (
//   premiseAddress: SiteDetails | undefined
// ) => {
//   return {
//     state: premiseAddress?.state,
//     buildingName: premiseAddress?.building_name,
//     suburb: premiseAddress?.suburb,
//     postcode: premiseAddress?.postcode,
//     unitNumber: premiseAddress?.unit_number,
//     premiseType: premiseAddress?.unit_type,
//     streetNumber: premiseAddress?.street_number,
//     streetName: premiseAddress?.street_name,
//     level: premiseAddress?.floor_number,
//     streetSuffix: premiseAddress?.street_suffix,
//     streetType: premiseAddress?.street_type,
//   };
// };

type RequestFiltersRestDTO = {
  state?: State[];
  status?: RequestStatusFilterEnum[];
  service_type?: ServiceTypeEnum[];
  connection_type?: ConnectionTypeFilterOptionEnum[];
  operational_team?: OperationalTeamEnum[];
  requesting_company_id?: string[];
  project_id?: string[];
  created_from?: string;
  created_to?: string;
  site_supervisor_name?: string[];
  collaborator_id?: string[];
};

const toOperationalTeamEnums = (
  operationalTeamDisplayValues: string[]
): OperationalTeamEnum[] => {
  return operationalTeamDisplayValues.reduce<OperationalTeamEnum[]>(
    (acc, displayValue) => {
      switch (displayValue) {
        case "Tier 1":
          acc.push(OperationalTeamEnum.TIER_1);
          break;
        case "Tier 2":
          acc.push(OperationalTeamEnum.TIER_2);
          break;
        case "Tier 3":
          acc.push(OperationalTeamEnum.TIER_3);
          break;
        case "CES":
          acc.push(OperationalTeamEnum.CES);
          break;
        case "MSBG - Council":
          acc.push(OperationalTeamEnum.MSBG_COUNCIL);
          break;
        case "MSBG - Telco":
          acc.push(OperationalTeamEnum.MSBG_TELCO);
          break;
        default:
          break;
      }
      return acc;
    },
    []
  );
};

const toConnectionTypeFilterOptionEnums = (
  requestTypeDisplayValues: string[]
): ConnectionTypeFilterOptionEnum[] => {
  return requestTypeDisplayValues.reduce<ConnectionTypeFilterOptionEnum[]>(
    (acc, displayValue) => {
      switch (displayValue) {
        case "Permanent":
          acc.push(ConnectionTypeFilterOptionEnum.PERMANENT);
          break;
        case "Temp in perm":
          acc.push(ConnectionTypeFilterOptionEnum.TEMPPERM);
          break;
        case "Temporary":
          acc.push(ConnectionTypeFilterOptionEnum.TEMPORARY);
          break;
        case "Service line and meter":
          acc.push(
            ConnectionTypeFilterOptionEnum.SERVICE_CONNECTION_AND_METER_FIX
          );
          break;
        case "Meter":
          acc.push(ConnectionTypeFilterOptionEnum.METER_FIX);
          break;
        case "Service line":
          acc.push(ConnectionTypeFilterOptionEnum.SERVICE_CONNECTION);
          break;
        case "Close account":
          acc.push(ConnectionTypeFilterOptionEnum.CLOSE_ACCOUNT);
          break;
        case "Close and transfer":
          acc.push(ConnectionTypeFilterOptionEnum.CLOSE_AND_TRANSFER);
          break;
        case "Pre install NBN":
          acc.push(ConnectionTypeFilterOptionEnum.PRE_INSTALL_NBN);
          break;
        case "Can Retail":
          acc.push(ConnectionTypeFilterOptionEnum.CAN_RETAIL);
          break;
        case "Address not found":
          acc.push(ConnectionTypeFilterOptionEnum.ADDRESS_NOT_FOUND);
          break;
        default:
          break;
      }
      return acc;
    },
    []
  );
};

const toServiceTypeEnums = (
  serviceTypeDisplayValues: string[]
): ServiceTypeEnum[] => {
  return serviceTypeDisplayValues.reduce<ServiceTypeEnum[]>(
    (acc, displayValue) => {
      switch (displayValue) {
        case "Electricity":
          acc.push(ServiceTypeEnum.ELECTRICITY);
          break;
        case "Natural gas":
          acc.push(ServiceTypeEnum.GAS);
          break;
        case "Internet":
          acc.push(ServiceTypeEnum.INTERNET);
          break;
        default:
          break;
      }
      return acc;
    },
    []
  );
};

const toRequestStatusFilterEnums = (
  requestStatusDisplayValues: string[]
): RequestStatusFilterEnum[] => {
  return requestStatusDisplayValues.reduce<RequestStatusFilterEnum[]>(
    (acc, displayValue) => {
      switch (displayValue) {
        case RequestStatusFilterOption.DRAFT:
          acc.push(RequestStatusFilterEnum.DRAFT);
          break;
        case RequestStatusFilterOption.ASSIGNED:
          acc.push(RequestStatusFilterEnum.DRAFT_ASSIGNED);
          break;
        case RequestStatusFilterOption.RECEIVED:
          acc.push(RequestStatusFilterEnum.IN_PROGRESS);
          acc.push(RequestStatusFilterEnum.FAILED);
          break;
        case RequestStatusFilterOption.CONNECTION_REQUESTED:
          acc.push(RequestStatusFilterEnum.NMI_REQUESTED);
          acc.push(RequestStatusFilterEnum.NMI_ALLOCATED);
          acc.push(RequestStatusFilterEnum.CONNECTION_REQUESTED);
          acc.push(RequestStatusFilterEnum.SERVICE_LINE_REQUESTED);
          acc.push(RequestStatusFilterEnum.METER_REQUESTED);
          break;
        case RequestStatusFilterOption.CONNECTED:
          acc.push(RequestStatusFilterEnum.CONNECTED);
          break;
        case RequestStatusFilterOption.BOOK_CLOSURE:
          acc.push(RequestStatusFilterEnum.COMPLETED);
          break;
        case RequestStatusFilterOption.CANCELLED:
          acc.push(RequestStatusFilterEnum.CANCELLED);
          break;
        case RequestStatusFilterOption.PAUSED:
          acc.push(RequestStatusFilterEnum.REPLY_NEEDED);
          break;
        case RequestStatusFilterOption.UNREAD_MESSAGE:
          acc.push(RequestStatusFilterEnum.NEW_MESSAGE);
          break;
        case RequestStatusFilterOption.ALL_DONE:
          acc.push(RequestStatusFilterEnum.FINISHED);
          break;
        case RequestStatusFilterOption.CLOSED:
          acc.push(RequestStatusFilterEnum.CLOSED);
          break;
        default:
          break;
      }
      return acc;
    },
    []
  );
};

export const toRequestFiltersRestDTO = (
  filters: RequestFilter
): RequestFiltersRestDTO => {
  return {
    state:
      (filters.state
        ?.map((it) => Object.values(State).find((v) => v === it))
        .filter(Boolean) as State[]) ?? undefined,
    status: filters.status
      ? toRequestStatusFilterEnums(filters.status)
      : undefined,
    service_type: filters.serviceType
      ? toServiceTypeEnums(filters.serviceType)
      : undefined,
    connection_type: filters.requestType
      ? toConnectionTypeFilterOptionEnums(filters.requestType)
      : undefined,
    operational_team: filters.operationalTeam
      ? toOperationalTeamEnums(filters.operationalTeam)
      : undefined,
    requesting_company_id: filters.requestingCompanyId
      ? [filters.requestingCompanyId]
      : undefined,
    created_from: normaliseDate(filters.createdFrom ?? null, null) ?? undefined,
    created_to: normaliseDate(filters.createdTo ?? null, null) ?? undefined,
  };
};

export function mapInternetStatusLookupRequest(values: FormValues) {
  return {
    loc_id: "", // For READ-only case
    floor_number: values.premiseAddress.level,
    unit_number: values.premiseAddress.unitNumber,
    unit_type: values.premiseAddress.premiseType,
    property_type: values.propertyType,
    site_details: mapSiteDetails(values),
  };
}

// Helper function to map UpdateEligibilityFormValues to FormValues for API call
export function mapToFormValuesInternet(
  values: UpdateEligibilityFormValues
): Partial<FormValues> {
  const isDefined = <T>(field: T | null | undefined): field is T =>
    field !== null && field !== undefined;

  const premise = values.addressObj as any;

  const premiseAddress = {
    ...(isDefined(premise?.site_details?.id) && {
      siteId: premise.site_details.id,
    }),
    ...(isDefined(values.state) && { state: values.state }),
    ...(isDefined(values.suburb) && { suburb: values.suburb }),
    ...(isDefined(values.postcode) && { postcode: values.postcode }),
    ...(isDefined(values.unitNumber) && { unitNumber: values.unitNumber }),
    ...(isDefined(values.premiseType) && { premiseType: values.premiseType }),
    ...(isDefined(values.streetNumber) && {
      streetNumber: values.streetNumber,
    }),
    ...(isDefined(values.lotNumber) && { lotNumber: values.lotNumber }),
    ...(isDefined(values.streetName) && { streetName: values.streetName }),
    ...(isDefined(values.streetSuffix) && {
      streetSuffix: values.streetSuffix,
    }),
    ...(isDefined(values.streetType) && { streetType: values.streetType }),
  };

  return {
    ...(isDefined(premise?.id) && { id: premise.id }),
    locId: "",
    siteSupervisor: premise?.site_details?.site_supervisor || "",
    requestingCompany: premise?.site_details?.project?.requesting_company
      ? {
          msaNumber: premise.site_details.project.requesting_company.msa_number,
        }
      : null,
    ...(isDefined(premise?.site_details?.project?.id) && {
      projectId: premise.site_details.project.id,
    }),
    ...(isDefined(premise?.property_type) && {
      propertyType: premise.property_type,
    }),
    ...(isDefined(premise?.site_details?.site_access) && {
      propertyAccess: premise.site_details.site_access ? "yes" : "no",
    }),
    ...(isDefined(premise?.site_details?.site_access_details) && {
      preventingAccess: premise.site_details.site_access_details,
    }),
    premiseAddress,
  } as Partial<FormValues>;
}

const mapSiteDetails = (values: FormValues) => ({
  id: values.premiseAddress.siteId,
  lot_number: values.premiseAddress.lotNumber,
  street_number: values.premiseAddress.streetNumber,
  street_name: values.premiseAddress.streetName,
  street_suffix: values.premiseAddress.streetSuffix,
  street_type: values.premiseAddress.streetType,
  postcode: values.premiseAddress.postcode,
  dp_number: values.premiseAddress.dpNumber,
  block_number: values.premiseAddress.blockNumber,
  section_number: values.premiseAddress.sectionNumber,
  suburb: values.premiseAddress.suburb,
  state: values.premiseAddress.state,
  building_name: values.premiseAddress.buildingName,
  requesting_company: {
    msa_number: values.requestingCompany?.msaNumber,
  },
  project: { id: values.projectId },
  site_supervisor: values.siteSupervisor,
  site_access: values.propertyAccess === "yes",
  site_access_details: values.preventingAccess,
});
export const mapBulkDuplicateDraftRequest = (
  values: BulkDuplicateFormValues
) => {
  const { premises, addressObj, addressParts } = values;
  const updatedPremises = premises.map((premise) => {
    const updatedSiteDetails = { ...addressObj.site_details };

    const siteDetailsMapping: Record<
      string,
      keyof typeof addressObj.site_details
    > = {
      street_number: "street_number",
      lot_number: "lot_number",
      block_number: "block_number",
      section_number: "section_number",
      dp_number: "dp_number",
    };

    Object.keys(addressParts).forEach((key) => {
      const siteDetailsKey = siteDetailsMapping[key];
      if (
        siteDetailsKey &&
        premise[key as keyof BulkDuplicatePremises] !== undefined
      ) {
        updatedSiteDetails[siteDetailsKey] = premise[
          key as keyof BulkDuplicatePremises
        ] as any;
      }
    });

    return {
      ...addressObj,
      id: "",
      unit_number: premise.unit_number ?? addressObj.unit_number,
      floor_number: premise.floor_number ?? addressObj.floor_number,
      site_details: {
        ...updatedSiteDetails,
        id: "",
      },
    };
  });
  return {
    premises: updatedPremises,
    services: values.services.filter((service) =>
      values.selectedServices.includes(service.service_type)
    ),
  };
};
